// Structural comparison of two config payloads. Pure — the server enforces the same rules with
// GameConfigMergeEngine, and these functions are deliberately a transliteration of it (and of the
// nitro-play `config-engine` it was ported from) so the editor can say what changed, and refuse a
// delete, before the round trip rather than after it. Where the two could drift, prefer matching the
// server: a client that blocks a save the server would accept is worse than no client check at all.

export const tryParseJson = (text) => {
    if (typeof text !== 'string' || !text.trim()) return { ok: true, value: {} };
    try {
        return { ok: true, value: JSON.parse(text) };
    } catch (e) {
        return { ok: false, error: e.message };
    }
};

export const stringifyJson = (value) => JSON.stringify(value ?? {}, null, 2);

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Every OBJECT-KEY path in a value, dotted — `{a:{b:1}}` → `{"a", "a.b"}`.
 *
 * Intermediate keys count, not just leaves. That distinction is the whole correctness of the delete
 * guard below: a parent's `{"flags":{}}` contributes the path `flags`, so a child that fills it in
 * as `{"flags":{"x":1}}` still has `flags` and has deleted nothing. Collecting leaves only would
 * report `flags` as removed and block a legal edit — which is exactly what this used to do.
 *
 * Only plain objects are descended. Arrays and scalars are opaque leaves, as in the server engine:
 * an override replaces a whole collection rather than merging into it, so array indices are not
 * paths anyone can inherit.
 */
export const objectKeyPaths = (value, prefix = '', out = new Set()) => {
    if (!isObject(value)) return out;
    Object.keys(value).forEach((key) => {
        const path = prefix ? `${prefix}.${key}` : key;
        out.add(path);
        objectKeyPaths(value[key], path, out);
    });
    return out;
};

/**
 * Keys the parent defines that this payload no longer has. An override may replace a value or add a
 * new one, but it can never take a key away — a player would end up with a config missing a field
 * the game expects.
 *
 * Mirrors `GameConfigMergeEngine.findDeletedKeys`.
 */
export const findDeletedKeys = (parent, next) => {
    const to = objectKeyPaths(next);
    return [...objectKeyPaths(parent)].filter((path) => !to.has(path)).sort();
};

/**
 * Structural diff of `base` → `next`: the keys whose value was added or changed, walked rather than
 * flattened. A subtree the child introduces is ONE `added` entry carrying the whole subtree, not one
 * entry per leaf inside it — a new bundle of eight fields is one decision by the author and reads as
 * one row. Deletions are never reported; they violate the inheritance contract and are surfaced by
 * `findDeletedKeys` instead.
 */
export const diffJson = (base, next, prefix = '') => {
    const entries = [];
    if (isObject(base) && isObject(next)) {
        Object.keys(next).forEach((key) => {
            const path = prefix ? `${prefix}.${key}` : key;
            if (!(key in base)) entries.push({ path, kind: 'added', after: next[key] });
            else entries.push(...diffJson(base[key], next[key], path));
        });
        return entries;
    }
    // Leaf or array: compared whole, because an override replaces it whole.
    if (JSON.stringify(base) !== JSON.stringify(next)) {
        entries.push({ path: prefix || '(root)', kind: 'changed', before: base, after: next });
    }
    return entries;
};

/**
 * Pretty-print tuned for line diffing: a value whose compact form is short stays on one line, while
 * a larger array/object expands one element or key per line. That keeps an array of small records (a
 * reward table, a store's bundles) to one row per element, so the diff highlights the entries that
 * actually changed instead of marking one enormous line as different.
 */
export const formatJsonForDiff = (value, pad = '') => {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    const compact = JSON.stringify(value);
    if (compact.length <= 120) return compact;
    const inner = `${pad}  `;
    if (Array.isArray(value)) {
        return `[\n${value.map((v) => inner + formatJsonForDiff(v, inner)).join(',\n')}\n${pad}]`;
    }
    const rows = Object.entries(value)
        .map(([k, v]) => `${inner}${JSON.stringify(k)}: ${formatJsonForDiff(v, inner)}`);
    return `{\n${rows.join(',\n')}\n${pad}}`;
};

/**
 * Line-level diff (LCS). Used for formats with no structure to walk, and to render one changed value
 * inside the structured diff. Config payloads are small, so the O(n·m) table is fine.
 */
export const lineDiff = (before, after) => {
    const a = String(before ?? '').split('\n');
    const b = String(after ?? '').split('\n');
    const n = a.length;
    const m = b.length;

    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i -= 1) {
        for (let j = m - 1; j >= 0; j -= 1) {
            dp[i][j] = a[i] === b[j]
                ? dp[i + 1][j + 1] + 1
                : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }

    const out = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) { out.push({ type: 'same', text: a[i] }); i += 1; j += 1; }
        else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: 'del', text: a[i] }); i += 1; }
        else { out.push({ type: 'add', text: b[j] }); j += 1; }
    }
    while (i < n) { out.push({ type: 'del', text: a[i] }); i += 1; }
    while (j < m) { out.push({ type: 'add', text: b[j] }); j += 1; }
    return out;
};

export const diffStats = (lines) => lines.reduce(
    (acc, l) => {
        if (l.type === 'add') acc.added += 1;
        else if (l.type === 'del') acc.removed += 1;
        return acc;
    },
    { added: 0, removed: 0 },
);
